import { isFoundingMember } from 'Utils/Plans';
import BANNER_CONFIG from 'Config/BannerCatalog.json';
import { removeCustomerSegment } from 'Api/AccountApi';
import { getShippingAddress } from './Shipping';
import { getPromoCode } from './PaymentMethods';
import { getDefaultConfig } from './Store';
import { logger } from './Logger';
import { getErrorMessageFromCode } from 'Api/EvergentApi';

export const parseSubscriptions = ({ isUserSubscribed = false, serviceDetails = [] }, productSkus) => {
  // parse serviceDetails
  const subscriptions = serviceDetails
    .reduce((acc, item) => {
      const pkgDetails = item.packageDetails.map((pkg) => {
        const productId = pkg.productId.map((p) => p.productId);
        return { ...pkg, appId: item.appId, productId };
      });
      return acc.concat(pkgDetails);
    }, [])
    .filter((sub) => sub.productId.some((id) => Object.values(productSkus).includes(id))); // filter by productSku [IMP]

  return {
    hasSubscription: isUserSubscribed && subscriptions.length > 0,
    subscriptions,
  };
};

export const checkSubscription = ({ hasSubscription = false, subscriptions = [] }) => {
  if (hasSubscription) {
    /*
      when "operationMode" prop is present on any package, that includes values like: "Downgrade" or "Upgrade"
      it suggests, user have future/next bill package and operation that changed the service.
      for these scenarios, startDate and endDate of the next/future package will be endDate of
      current package with status: "Final Bill".
    */

    const nextSubscription = subscriptions.find((s) => s.operationMode);
    const activeSubscription = subscriptions.find((s) => !s.operationMode);
    if (activeSubscription) {
      const activeSubscriptionId = activeSubscription.serviceID;
      const isFounder = isFoundingMember(activeSubscriptionId, activeSubscription.startDate);
      const validityTill = activeSubscription.validityTill || 0;
      return {
        hasSubscription,
        activeSubscriptionId,
        isFounder,
        validityTill,
        nextSubscription,
      };
    }
  }
  return null;
};

/**
 * Removes Promotion Segment from account after successful subscription to the promo SKU.
 */
export const processSegment = (subscription, selectedPlan) => {
  if (!subscription.accountPromotionSegment.segmentName) return;
  const promoBannerSKU = BANNER_CONFIG[subscription.accountPromotionSegment.segmentType].bannerSKU;
  if (selectedPlan.packageID === promoBannerSKU) {
    removeCustomerSegment(subscription.accountPromotionSegment.segmentName);
  }
};

const transformParameters = (obj) => {
  return Object.entries(obj).reduce((acc, [paramName, paramValue]) => {
    return acc.concat([{ paramName, paramValue }]);
  }, []);
};

export const getSubscriptionParameters = ({ changeType = '', autoCouponCode = false }) => {
  const parametersObj = {};
  if (changeType) {
    parametersObj['Operation Type'] = changeType; // values: "Downgrade", "Upgrade";
  }
  if (autoCouponCode) {
    parametersObj.couponCodeEntry = 'auto';
  }
  const subscriptionParams = transformParameters(parametersObj);

  // all parameters
  return subscriptionParams;
};

export const getChangeSubscriptionType = ({ subscription, selectedPlan }) => {
  // changeTypes: "Downgrade", "Upgrade"
  const { activeSubscriptionTier } = subscription;
  const { tier } = selectedPlan;
  if (tier < activeSubscriptionTier) {
    return 'Downgrade';
  }
  if (tier > activeSubscriptionTier) {
    return 'Upgrade';
  }
  return undefined;
};

export const isDowngradeSubscription = (subscription, selectedPlan) =>
  getChangeSubscriptionType({ subscription, selectedPlan }) === 'Downgrade';

export const isUpgradeSubscription = (subscription, selectedPlan) =>
  getChangeSubscriptionType({ subscription, selectedPlan }) === 'Upgrade';

export const getAddSubscriptionOperationType = ({ subscription }) => {
  return subscription.isChurnedRestartAvailable ? 'Restartable' : undefined;
};

export const getSubscriptionServices = (plans) => {
  let service = [];
  if (plans.isPlanSelected && plans.selectedPlan) {
    const { selectedPlan } = plans;
    service = [
      {
        promotionId: selectedPlan.promotionID,
        serviceType: 'PACKAGE',
        serviceId: selectedPlan.planId,
        quantity: '1',
      },
    ];
  }
  return { service };
};

export const getAddSubscriptionRequest = (data) => {
  const { evergentApiKey } = getDefaultConfig();
  const {
    plans,
    promoCode,
    checkout,
    user,
    paymentmethodInfo = {},
    isSkipDefaultPromo,
    subscription,
    transactionReferenceMsg,
  } = data;
  const shippingAddress = getShippingAddress(plans, checkout);
  const services = getSubscriptionServices(plans);
  const parameters = getSubscriptionParameters({ autoCouponCode: !!promoCode.emailCoupon });
  const operationType = getAddSubscriptionOperationType({ subscription });
  const couponCode = getPromoCode(promoCode);
  const paymentMethodInfoData = {
    ...paymentmethodInfo,
    transactionReferenceMsg,
  };
  return {
    accessToken: user.accessToken,
    AddSubscriptionRequestMessage: {
      apiKey: evergentApiKey,
      channelPartnerID: 'FOXUSA',
      cpCustomerID: user.profileId,
      isSkipDefaultPromo,
      couponCode,
      parameters,
      services,
      paymentmethodInfo: paymentMethodInfoData,
      shippingAddress,
      operationType,
    },
  };
};

export const getChangeSubscriptionRequest = (data) => {
  const { evergentApiKey } = getDefaultConfig();
  const { plans, subscription, promoCode, checkout, user, paymentmethodInfo = {} } = data;

  const { selectedPlan } = plans;
  const changeType = getChangeSubscriptionType({ subscription, selectedPlan });
  const shippingAddress = getShippingAddress(plans, checkout);
  const parameters = getSubscriptionParameters({ changeType, autoCouponCode: !!promoCode.emailCoupon });
  const couponCode = getPromoCode(promoCode);

  return {
    ChangeServiceRequestMessage: {
      changeType,
      apiKey: evergentApiKey,
      channelPartnerID: 'FOXUSA',
      cpCustomerID: user.profileId,
      couponCode,
      promotionId: selectedPlan.promotionID,
      newServiceID: selectedPlan.planId,
      paymentmethodInfo,
      parameters,
      shippingAddress,
    },
  };
};

export const addSubscriptionResponse = (data) => {
  logger.apiResponse('add subscription', data);
  try {
    const addSubscriptionsResponse = data && data.addSubscriptionsResponseMessage;
    if (!addSubscriptionsResponse || !addSubscriptionsResponse.responseCode) {
      throw Error('Invalid API response: addSubscriptions');
    }
    // only addSubscription returns orderID, changeService and paypal api's return orderId (lowercase d)
    const { responseCode, orderID = '', failureMessage: { errorCode = null } = {} } = addSubscriptionsResponse;
    const errorMessage = getErrorMessageFromCode(errorCode);
    return { isSuccess: responseCode === '1', orderID, errorMessage, errorCode };
  } catch (error) {
    logger.apiError('add subscription', data, error);
    return {
      errorCode: 'EV_API_ERROR',
      errorMessage: error.message,
      isSuccess: false,
      orderID: '',
    };
  }
};

export const changeSubscriptionResponse = (data) => {
  logger.apiResponse('change service', data);
  try {
    const changeServiceResponse = data && data.ChangeServiceResponseMessage;
    if (!changeServiceResponse || !changeServiceResponse.responseCode) {
      throw Error('Invalid API response: changeService');
    }
    const { responseCode, orderId = '', failureMessage: { errorCode = null } = {} } = changeServiceResponse;
    const errorMessage = getErrorMessageFromCode(errorCode);
    return { isSuccess: responseCode === '1', orderID: orderId, errorMessage, errorCode };
  } catch (error) {
    logger.apiError('change service', data, error);
    return {
      errorCode: 'EV_API_ERROR',
      errorMessage: error.message,
      isSuccess: false,
      orderID: '',
    };
  }
};
